/**
 * Console output, switched off in the deployed browser build.
 *
 * <b>Why this exists.</b> The desktop app's console is behind devtools that a user has to open on
 * purpose; the browser build's is one keystroke away on a machine in a clinic, and everything this
 * app does is next to patient data. A stray `console.log(patient)` is not a tidiness problem there,
 * it is a disclosure. Routing logging through one place means "nothing is printed in production"
 * is a property of a single module rather than of 147 call sites staying disciplined.
 *
 * <b>What is switched off, and where.</b> Only the production browser build — `__WEB__ && !__DEV__`.
 * The desktop renderer keeps its console in every build, because support reads it from a doctor's
 * machine over a screen share and there is no other way in. `npm run dev:web` keeps it too.
 *
 * <b>The escape hatch.</b> A support engineer can turn it back on for one browser by setting
 * `localStorage['dictaris:debug'] = '1'` and reloading. Without it, diagnosing a problem that only
 * happens in production means shipping a build, which is not a plan. It is read once at startup,
 * so turning it on or off takes a reload — deliberate, because logging that can appear halfway
 * through a session produces logs that lie about what was recorded.
 */

const DEBUG_OVERRIDE_KEY = 'dictaris:debug';

/**
 * `localStorage` is not always reachable — a browser with site data blocked throws on access
 * rather than returning null, and this runs before anything has had a chance to catch it. A
 * failure here means "no override", never a crash on startup.
 */
function debugOverrideEnabled(): boolean {
  try {
    return globalThis.localStorage?.getItem(DEBUG_OVERRIDE_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * `__WEB__` and `__DEV__` are Vite `define` constants, and they only exist once a bundler has
 * substituted them. Plenty of this code is also imported directly by the unit tests, which
 * `node --test` runs without Vite — reading the bare identifiers there throws a ReferenceError at
 * import time and takes the whole test file with it, which is exactly how this was found.
 *
 * Reading them through `typeof` makes the module importable with or without a build step, and
 * still folds in a real one: `define` substitutes inside `typeof` as well, so the check becomes
 * `typeof true === 'undefined'` and is resolved at build time. Outside a build the fallbacks are
 * "not the web, and in development" — the pair that leaves logging on, because a test or a script
 * that silently printed nothing would be worse than one that printed.
 */
const isWeb: boolean = typeof __WEB__ !== 'undefined' && __WEB__;
const isDev: boolean = typeof __DEV__ === 'undefined' || __DEV__;

/** Read once, at module load. */
const enabled: boolean = !isWeb || isDev || debugOverrideEnabled();

function noop(): void {
  /* Production browser build: nothing is printed. */
}

/**
 * Bound references to the real methods rather than wrapper functions that forward to them.
 *
 * This is the part worth not simplifying. A wrapper like `(...args) => console.log(...args)` works,
 * but devtools then reports every line as coming from this file — the "source" link beside each
 * message points here instead of at the code that logged, and stack traces gain a frame that means
 * nothing. Binding hands back the original function, so the call site is preserved exactly as if
 * `console.log` had been called directly.
 */
export const log = {
  /** Mirrors `console.log`. */
  log: enabled ? console.log.bind(console) : noop,
  /** Mirrors `console.info`. */
  info: enabled ? console.info.bind(console) : noop,
  /** Mirrors `console.warn`. */
  warn: enabled ? console.warn.bind(console) : noop,
  /**
   * Mirrors `console.error`.
   *
   * Silenced in production too, which is the one call worth being deliberate about: the web build
   * ships source maps precisely so a stack trace from a doctor's browser is readable, and this
   * turns that off by default. The override above is what gets it back for one machine, which is
   * the trade this makes — errors are quiet for everyone rather than half-quiet for everyone.
   */
  error: enabled ? console.error.bind(console) : noop,
  /** Mirrors `console.debug`. */
  debug: enabled ? console.debug.bind(console) : noop,
} as const;

/**
 * Replaces the global console's output methods with no-ops in the production browser build.
 *
 * <b>The wrapper above cannot do this job on its own.</b> It covers the code that imports it, and
 * nothing else: a call site anyone forgets to migrate, a `console.log` added in a hurry, and every
 * dependency in `node_modules` all still print. Since the requirement is that nothing reaches the
 * console, the global has to be closed too.
 *
 * Called from the browser entry point before the app's module graph is evaluated, so there is no
 * window in which something can log first.
 *
 * `console.assert`, `console.table`, `console.trace`, `console.dir` and the timing and grouping
 * methods are included: they are all output. `console.time`/`timeEnd` are replaced rather than
 * left, because an unmatched `timeEnd` prints a warning of its own.
 */
export function silenceConsoleOnProdWeb(): void {
  if (enabled) return;

  const silenced: Array<keyof Console> = [
    'log',
    'info',
    'warn',
    'error',
    'debug',
    'trace',
    'dir',
    'dirxml',
    'table',
    'assert',
    'count',
    'countReset',
    'group',
    'groupCollapsed',
    'groupEnd',
    'time',
    'timeEnd',
    'timeLog',
  ];

  for (const method of silenced) {
    // Assigning onto the host console rather than replacing the object: something may already
    // hold a reference to `console` itself, and replacing it would leave that reference live.
    (console as unknown as Record<string, unknown>)[method] = noop;
  }
}

/** Whether anything will actually be printed. Exported for the one test that asserts it. */
export function isLoggingEnabled(): boolean {
  return enabled;
}
